import { cn } from '@/lib/utils'
import { DiagonalLines } from '@/components/ui/diagonal-lines'
import type { DiagonalLinesCorner } from '@/components/ui/diagonal-lines'
import type { BrandColor, GradientPreset, OverlayConfig, DiagonalCorner } from '@/types'

// Compile-time check: DiagonalCorner must stay assignable to DiagonalLinesCorner
const _: DiagonalLinesCorner = '' as DiagonalCorner
void _

export const BRAND_HEX: Record<BrandColor, string> = {
  orange:     '#FF9D76',
  blue:       '#76F1FF',
  purple:     '#C176FF',
  yellow:     '#FFE176',
  black:      '#171717',
  white:      '#f2f2f3',
  background: 'var(--background)', // resolves per theme (light/dark)
}

// CSS expression that yields the color with the given 0–1 alpha.
// Uses color-mix so it works with both hex literals and CSS var() colors.
function cssColorWithAlpha(color: BrandColor, alpha: number): string {
  const base = BRAND_HEX[color]
  const pct = Math.round(alpha * 100)
  return `color-mix(in srgb, ${base} ${pct}%, transparent)`
}

export const GRADIENT_CSS: Record<GradientPreset, string> = {
  'full-brand':    'linear-gradient(135deg, #FF9D76, #C176FF, #76F1FF, #FFE176)',
  'orange-purple': 'linear-gradient(135deg, #FF9D76, #C176FF)',
  'blue-yellow':   'linear-gradient(135deg, #76F1FF, #FFE176)',
  'orange-blue':   'linear-gradient(135deg, #FF9D76, #76F1FF)',
  'purple-yellow': 'linear-gradient(135deg, #C176FF, #FFE176)',
  'dark-tonal':    'linear-gradient(135deg, #171717, rgba(23,23,23,0))',
}

interface SlotOverlayProps {
  overlay: OverlayConfig
  className?: string
}

export function SlotOverlay({ overlay, className }: SlotOverlayProps) {
  if (overlay.type === 'none') return null

  if (overlay.type === 'stripe') {
    return (
      <DiagonalLines
        className={cn('absolute inset-0 pointer-events-none', className)}
        color={BRAND_HEX[overlay.color]}
        opacity={overlay.opacity / 100}
        spacing={overlay.spacing}
        strokeWidth={overlay.strokeWidth}
        corner={overlay.corner as DiagonalLinesCorner}
      />
    )
  }

  if (overlay.type === 'gradient') {
    return (
      <div
        className={cn('absolute inset-0 pointer-events-none', className)}
        style={{ background: GRADIENT_CSS[overlay.preset], opacity: overlay.opacity / 100 }}
      />
    )
  }

  if (overlay.type === 'solid') {
    return (
      <div
        className={cn('absolute inset-0 pointer-events-none', className)}
        style={{ background: BRAND_HEX[overlay.color], opacity: overlay.opacity / 100 }}
      />
    )
  }

  // fade — opacity baked into color-mix stops so theme-aware colors work too
  const from = cssColorWithAlpha(overlay.color, overlay.fromOpacity / 100)
  const to   = cssColorWithAlpha(overlay.color, overlay.toOpacity   / 100)
  const dir  = overlay.direction.replace(/-/g, ' ') // 'to-bottom' → 'to bottom'
  return (
    <div
      className={cn('absolute inset-0 pointer-events-none', className)}
      style={{ background: `linear-gradient(${dir}, ${from}, ${to})` }}
    />
  )
}
